import React from "react";

import { formatNumericCurrency, useTranslation } from "utils";

import { Card, CropRotationCardProps, Flex, Icon, MonthlyEmptyCropCard, Tag, TooltipWrapper } from "components";

export type MonthlyCropCardProps = Partial<CropRotationCardProps> & {
  nameContainerRef: React.RefObject<HTMLParagraphElement>;
  nameRef: React.RefObject<HTMLParagraphElement>;
  shouldShowNameTooltip: boolean;
  shouldHideIcons: boolean;
  shouldShowEuroIcon: boolean;
  shouldHideBaselineTag: boolean;
  isNarrow: boolean;
  isSelected: boolean;
  isEmptyCard?: boolean;
  isBaseline?: boolean;
  isConfirmed?: boolean;
  isFetching: boolean;
  isReportingYear?: boolean;
  cropMixId?: string;
};

// card from Soilmates (monthly view card)
export const MonthlyCropCard = ({
  cropName,
  hasResidue,
  payout,
  currency,
  year,
  recommendations,
  dataTestId,
  isSelected,
  isNarrow,
  nameContainerRef,
  nameRef,
  shouldShowNameTooltip,
  shouldHideIcons,
  shouldShowEuroIcon,
  shouldHideBaselineTag,
  cropType,
  isEmptyCard,
  isBaseline,
  isConfirmed,
  isMandatory,
  isFetching,
  isReportingYear,
  simulationId,
  fieldSize,
  cropMixId,
}: MonthlyCropCardProps) => {
  const { t } = useTranslation("fieldTimeline");

  const generateTooltipContent = () => {
    const content = [];

    const shouldDisplayCropName = (shouldShowNameTooltip || isNarrow) && cropName;
    if (shouldDisplayCropName) content.push(cropName);

    if (shouldHideIcons || shouldHideBaselineTag) {
      if (isBaseline) content.push(t("cropCard.baseline"));
    }

    if (shouldHideIcons) {
      if (hasResidue) content.push(t("cropCard.residueLeftOnField"));
      if (payout) content.push(formatNumericCurrency(payout, { currency, decimals: 0 }));
      if (isConfirmed) content.push(t("cropCard.submitted"));
    }

    if (content.length === 0) return null;
    return (
      <Flex justify="flex-start" vertical>
        {content.map((element, i) => {
          if (i === 0)
            return (
              <span key={i} className="white-text">
                {element}
              </span>
            );
          return (
            <span key={i} className="white-text">
              {element}
            </span>
          );
        })}
      </Flex>
    );
  };

  return !isEmptyCard ? (
    <Card
      className={`crop-rotation-card layout ${isSelected ? "selected" : ""} ${!simulationId && isReportingYear ? "reporting-year" : ""} ${
        isNarrow ? "is-narrow" : ""
      }`}
      data-testid={`crop-rotation-card-${dataTestId}`}
    >
      <Flex align="center" justify="center" gap="0.5rem">
        <Flex
          ref={nameContainerRef}
          data-testid={`crop-rotation-card-${dataTestId}-header-name`}
          className={`name-container ${isNarrow ? "is-narrow" : ""}`}
          gap=".125rem"
        >
          {cropMixId && <Icon className="cmc-plant-icon" iconName="klim/plant-ai" />}
          <TooltipWrapper
            tooltip={
              shouldShowNameTooltip || isNarrow || shouldHideIcons
                ? {
                    placement: "top",
                    title: generateTooltipContent(),
                  }
                : {}
            }
          >
            <span ref={nameRef} className="name">
              {isNarrow ? "..." : cropName}
            </span>
          </TooltipWrapper>
        </Flex>
        <Flex align="center" justify="center" gap="0.5rem">
          {!shouldHideIcons && hasResidue && (
            <TooltipWrapper tooltip={{ placement: "top", title: t("cropCard.residueLeftOnField") }} className="residue-icon-container">
              <Icon iconName="tabler/growth" />
            </TooltipWrapper>
          )}
          {!shouldHideIcons && !shouldHideBaselineTag && isBaseline && <Tag className="baseline-tag" colour="grey" text={t("cropCard.baseline")} />}
          {!shouldHideIcons && isConfirmed && <Icon dataTestId={`crop-rotation-card-${dataTestId}-lock-icon`} iconName="tabler/lock" size="s" />}
          {!shouldHideIcons && payout && (
            <>
              {shouldShowEuroIcon ? (
                <TooltipWrapper
                  tooltip={{ placement: "top", title: formatNumericCurrency(payout, { currency, decimals: 0 }) }}
                  className="payout-icon-container"
                >
                  <Icon iconName="tabler/currency-euro" />
                </TooltipWrapper>
              ) : (
                <span className="payout-digits">{formatNumericCurrency(payout, { currency, decimals: 0 })}</span>
              )}
            </>
          )}
        </Flex>
      </Flex>
    </Card>
  ) : (
    <MonthlyEmptyCropCard
      payout={payout}
      currency={currency}
      isSelected={isSelected}
      cropType={cropType!}
      year={year}
      recommendations={recommendations}
      isNarrow={isNarrow}
      dataTestId={dataTestId}
      isBaseline={isBaseline}
      isConfirmed={isConfirmed}
      isRequired={isMandatory}
      isFetching={!!isFetching}
      simulationId={simulationId}
      fieldSize={fieldSize}
    />
  );
};
